import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import {
  BackLink,
  ContentMaxWidth,
  GhostButton,
  PageHeading,
  Stack,
} from '../../layout/styledComponents'
import { usePatient } from '../../context/PatientContext'
import * as svc from '../../services/messagesService'
import { parseUtcDate } from '../../utils/dates'

const Conversation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px 0;
  min-height: 50vh;
`

const Bubble = styled.div`
  align-self: ${(p) => (p.$mine ? 'flex-end' : 'flex-start')};
  max-width: 75%;
  padding: 10px 14px;
  border-radius: ${(p) => (p.$mine ? '18px 18px 4px 18px' : '18px 18px 18px 4px')};
  background: ${(p) =>
    p.$mine ? p.theme?.color?.primary || '#1F3A5F' : p.theme?.color?.bgRaised || '#fff'};
  color: ${(p) => (p.$mine ? 'white' : p.theme?.color?.ink || '#1F2937')};
  border: 1px solid
    ${(p) => (p.$mine ? 'transparent' : p.theme?.color?.border || '#E6E0D6')};
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
`

const Meta = styled.div`
  align-self: ${(p) => (p.$mine ? 'flex-end' : 'flex-start')};
  font-size: 11px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  margin: -4px 4px 4px;
`

const Composer = styled.form`
  display: flex;
  gap: 10px;
  position: sticky;
  bottom: 0;
  background: ${(p) => p.theme?.color?.bg || '#FAF7F2'};
  padding: 12px 0;
`

const Input = styled.input`
  flex: 1;
  padding: 12px 16px;
  border-radius: 999px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  font-family: inherit;
  font-size: 14px;
  &:focus {
    outline: 2px solid ${(p) => p.theme?.color?.primary || '#1F3A5F'};
    outline-offset: 1px;
  }
`

const SendBtn = styled.button`
  appearance: none;
  border: none;
  background: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  color: white;
  padding: 0 20px;
  border-radius: 999px;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

function timeLabel(iso) {
  if (!iso) return ''
  return parseUtcDate(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

export default function MessageThreadPage() {
  const { currentProfile } = usePatient()
  const { threadId: peerId } = useParams()
  const [messages, setMessages] = useState(null)
  const [loading, setLoading] = useState(true)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const scrollEndRef = useRef(null)

  const refresh = useCallback(async () => {
    if (!currentProfile || !peerId) return
    setLoading(true)
    try {
      const page = await svc.getThread(currentProfile, peerId)
      setMessages(page.items || [])
      // mark read on open
      await svc.markRead(currentProfile, peerId)
    } finally {
      setLoading(false)
    }
  }, [currentProfile, peerId])

  useEffect(() => {
    setMessages(null)
    refresh()
  }, [refresh])

  useEffect(() => {
    if (scrollEndRef.current) scrollEndRef.current.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  async function handleSend(e) {
    e?.preventDefault?.()
    const body = text.trim()
    if (!body) return
    setSending(true)
    try {
      const sent = await svc.send(currentProfile, peerId, { type: 'TEXT', text: body })
      if (sent) {
        setMessages((prev) => [...(prev || []), sent])
      }
      setText('')
    } catch (e) {
      // surface gracefully — no toast yet in this portal
      window.alert('Could not send right now. Try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <ContentMaxWidth>
      <Stack $gap="12px">
        <BackLink to="/patient-home/messages">Back to inbox</BackLink>

        <PageHeading>{peerId}</PageHeading>

        {loading && !messages ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <LoadingSpinner />
          </div>
        ) : (
          <Conversation>
            {(messages || []).map((m) => {
              const mine = m.direction === 'patient'
              return (
                <React.Fragment key={m.id}>
                  <Bubble $mine={mine}>{m.text}</Bubble>
                  <Meta $mine={mine}>{timeLabel(m.sentAt)}</Meta>
                </React.Fragment>
              )
            })}
            <div ref={scrollEndRef} />
          </Conversation>
        )}

        <Composer onSubmit={handleSend}>
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type a message"
            disabled={sending}
          />
          <SendBtn type="submit" disabled={!text.trim() || sending}>
            {sending ? '…' : 'Send'}
          </SendBtn>
        </Composer>
      </Stack>
    </ContentMaxWidth>
  )
}
